import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcIncreaseV1AccountNumberQueryServiceAccountNumberGet } from '@interxfi/api-client/generated/hooks';
import { useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcIncreaseV1AccountNumberState } from '@interxfi/types';
import { IncreaseAccountNumberEntityMenu } from '../../../../../../../../../../features/vendor/increase/account-number/increase-account-number-entity-menu';
import { IncreaseAccountNumberEventSummary } from '../../../../../../../../../../features/vendor/increase/account-number/increase-account-number-event';
import { getIncreaseAccountNumberStateListItems } from '../../../../../../../../../../features/vendor/increase/account-number/increase-account-number-list-items';
import { getIncreaseAccountNumberDetailPath } from '../../../../../../../../../../features/vendor/increase/account-number/increase-account-number-routing';
import { IncreaseAccountNumberStatusPill } from '../../../../../../../../../../features/vendor/increase/account-number/increase-account-number-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/increase/account-number/_account-number/$accountNumberId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `increase-account-number-${params.accountNumberId}`,
        label: 'Account Number',
        to: getIncreaseAccountNumberDetailPath(params.accountNumberId),
        suffix: (
          <CopyButton
            content={params.accountNumberId}
            label="Copy Account Number ID"
            tooltip={{ copy: params.accountNumberId, copied: 'Copied Account Number ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Increase Account Number'),
  component: IncreaseAccountNumberDetail,
});

function getListItems(data: ExcIncreaseV1AccountNumberState | undefined, isSimulated: boolean) {
  const items = getIncreaseAccountNumberStateListItems(data, { increaseId: !isSimulated });

  return {
    left: [
      {
        id: 'main',
        items: [
          items.data.spec.name,
          items.data.increaseId,
          items.data.spec.parentIncreaseAccountId,
          items.data.routingNumber,
          items.data.accountNumber,
          items.data.spec.inboundAchDebitAllowed,
        ],
      },
    ] as const satisfies ReadonlyListGroup[],
    right: [
      items.metadata.createdAt,
      items.metadata.updatedAt,
      items.accountNumberId,
      items.data.spec.programId,
      items.data.spec.accountId,
    ] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load Increase Account Number', 'view this Increase Account Number');

function IncreaseAccountNumberDetail() {
  const { accountNumberId } = Route.useParams();
  const request = { accountNumberId } as const;
  const { activeRealmIsSimulated } = useActiveRealmAPIContext();
  const { data, error, isPending } = useExcIncreaseV1AccountNumberQueryServiceAccountNumberGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.accountNumber, activeRealmIsSimulated), [data?.accountNumber, activeRealmIsSimulated]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <IncreaseAccountNumberStatusPill isPending={showPending} size="large" status={data?.accountNumber?.status} />
            </HorizontalStack>

            <IncreaseAccountNumberEntityMenu {...request} />
          </PageHeader>

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <IncreaseAccountNumberEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
